import api from "./api";

// PAEI (Producer / Administrator / Entrepreneur / Integrator) — one record per job.
// Backend has no "find by job_id" route, so we list and filter client-side.
// `findAll` shapes its response differently per role (an array, or a plain
// `{ message }` object when access is denied) — normalize both here.
const extractPaeiArray = (payload) => (Array.isArray(payload) ? payload : []);

export const normalizePaei = (p) => {
  if (!p || typeof p !== "object") return null;
  return {
    id: p.id,
    job_id: p.job_id,
    producer: Number(p.producer) || 0,
    administrator: Number(p.administrator) || 0,
    entrepreneur: Number(p.entrepreneur) || 0,
    integrator: Number(p.integrator) || 0,
    createdBy: p.createdBy ?? null,
    updatedBy: p.updatedBy ?? null,
    createdAt: p.createdAt ?? null,
    updatedAt: p.updatedAt ?? null,
  };
};

export const listPaei = async () => {
  const res = await api.get("/paei");
  return extractPaeiArray(res).map(normalizePaei).filter(Boolean);
};

export const getPaeiByJobId = async (jobId) => {
  if (!jobId) return null;
  try {
    const all = await listPaei();
    return all.find((p) => String(p.job_id) === String(jobId)) || null;
  } catch {
    return null;
  }
};

export const createPaei = async (payload) => {
  const body = {
    job_id: Number(payload.job_id),
    producer: Number(payload.producer) || 0,
    administrator: Number(payload.administrator) || 0,
    entrepreneur: Number(payload.entrepreneur) || 0,
    integrator: Number(payload.integrator) || 0,
  };
  const res = await api.post("/paei", body);
  return normalizePaei(res);
};

export const updatePaei = async (id, payload) => {
  if (!id) throw new Error("PAEI record id is required");
  const body = {
    producer: Number(payload.producer) || 0,
    administrator: Number(payload.administrator) || 0,
    entrepreneur: Number(payload.entrepreneur) || 0,
    integrator: Number(payload.integrator) || 0,
  };
  const res = await api.patch(`/paei/${id}`, body);
  return normalizePaei(res);
};

// Creates when no record exists yet for the job, otherwise updates in place.
// Self-heals a 409 (record already exists) by re-fetching and updating instead —
// covers the case where our initial load couldn't see the existing record.
export const savePaeiForJob = async (jobId, values, existingId) => {
  if (existingId) {
    return updatePaei(existingId, values);
  }
  try {
    return await createPaei({ job_id: jobId, ...values });
  } catch (e) {
    if (Number(e?.status) === 409) {
      const existing = await getPaeiByJobId(jobId);
      if (existing?.id) return updatePaei(existing.id, values);
    }
    throw e;
  }
};

export const deletePaei = async (id) => {
  if (!id) throw new Error("PAEI record id is required");
  return api.delete(`/paei/${id}`);
};
